import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAuthState } from '../app/store/authSlice.js'
import { createMetricsStreamService } from '../services/metricsStreamService.js'
import { listStatements } from '../services/statementsService.js'

const MAX_TREND_POINTS = 60 // 5 minutes at 5-second intervals

const useMetricsStream = ({ autoStart = true } = {}) => {
  const serviceRef = useRef(null)
  const stopRef = useRef(null)
  const headersRef = useRef({})
  const trendRef = useRef(new Map()) // Map<runtimeURI, dataPoints[]>

  const { apiKey, writeKey, isAuthenticated } = useSelector(selectAuthState)

  const [instanceId, setInstanceId] = useState(null)
  const [currentMetrics, setCurrentMetrics] = useState(null)
  const [connectionState, setConnectionState] = useState('idle')
  const [error, setError] = useState(null)
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null)
  const [nextRetry, setNextRetry] = useState(null)

  const [shouldStream, setShouldStream] = useState(autoStart && isAuthenticated)

  if (!serviceRef.current) {
    serviceRef.current = createMetricsStreamService({
      getAuthHeaders: () => headersRef.current,
    })
  }

  useEffect(() => {
    headersRef.current = (() => {
      const headers = {}
      if (apiKey) {
        headers['x-api-key'] = apiKey
      }
      if (writeKey) {
        headers.writekey = writeKey
      }
      return headers
    })()
  }, [apiKey, writeKey])

  // Fetch instance ID from statements list on mount
  useEffect(() => {
    if (!isAuthenticated || !apiKey) {
      return
    }

    let isCancelled = false

    const fetchInstanceId = async () => {
      try {
        console.log('[useMetricsStream] Fetching statements to get instance ID...')
        const response = await listStatements()
        if (isCancelled) return

        console.log('[useMetricsStream] Statements response:', response)

        // Extract instance from first statement
        if (response?.statements?.length > 0) {
          const firstStatement = response.statements[0]
          const instance = firstStatement.instanceName || firstStatement.cepInstance
          console.log('[useMetricsStream] Found instance ID:', instance)
          if (instance) {
            setInstanceId(instance)
          }
        } else {
          console.warn('[useMetricsStream] No statements found to extract instance ID')
        }
      } catch (err) {
        // Silently fail - user might not have any statements yet
        console.warn('[useMetricsStream] Failed to fetch instance ID:', err)
      }
    }

    fetchInstanceId()

    return () => {
      isCancelled = true
    }
  }, [isAuthenticated, apiKey])

  useEffect(() => {
    if (!isAuthenticated) {
      setShouldStream(false)
    } else if (autoStart && instanceId) {
      setShouldStream(true)
    }
  }, [isAuthenticated, autoStart, instanceId])

  const handleMetrics = useCallback((payload) => {
    if (!payload?.data) {
      return
    }

    const metrics = payload.data
    const receivedAt = payload.receivedAt ?? Date.now()

    // Update current metrics
    setCurrentMetrics(metrics)
    setLastUpdatedAt(receivedAt)

    // Add to trend data
    if (metrics.runtimeURI) {
      const samples = trendRef.current.get(metrics.runtimeURI) ?? []
      const nextSamples = [
        ...samples,
        {
          timestamp: metrics.timestamp,
          inputCount: metrics.inputCount,
          inputCountDelta: metrics.inputCountDelta,
          scheduleDepth: metrics.scheduleDepth,
        },
      ]
      if (nextSamples.length > MAX_TREND_POINTS) {
        nextSamples.splice(0, nextSamples.length - MAX_TREND_POINTS)
      }
      trendRef.current.set(metrics.runtimeURI, nextSamples)
    }
  }, [])

  const handleError = useCallback((streamError) => {
    setError(streamError)
  }, [])

  const handleStatusChange = useCallback((status, meta) => {
    setConnectionState(status)
    if (status === 'connected') {
      setNextRetry(null)
      setError(null)
    }
    if (status === 'stopped') {
      setNextRetry(null)
    }
    if (status === 'error' && meta) {
      setError(meta)
    }
  }, [])

  const handleReconnectScheduled = useCallback((details) => {
    setNextRetry(details)
  }, [])

  useEffect(() => {
    const service = serviceRef.current

    console.log('[useMetricsStream] Stream effect:', {
      shouldStream,
      isAuthenticated,
      hasApiKey: Boolean(apiKey),
      instanceId,
    })

    if (shouldStream && isAuthenticated && apiKey && instanceId) {
      console.log('[useMetricsStream] Starting stream with instanceId:', instanceId)

      // Clear old data when reconnecting (e.g., API key changed)
      setCurrentMetrics(null)
      setLastUpdatedAt(null)
      setError(null)
      trendRef.current.clear()

      const stop = service.start({
        instanceId,
        onMetrics: handleMetrics,
        onError: handleError,
        onStatusChange: handleStatusChange,
        onReconnectScheduled: handleReconnectScheduled,
      })
      stopRef.current = stop
      return () => {
        stop()
        stopRef.current = null
      }
    }

    console.log('[useMetricsStream] Not starting stream - missing required params')
    service.stop()
    stopRef.current = null
    setConnectionState('idle')
    setNextRetry(null)
    return undefined
  }, [
    shouldStream,
    isAuthenticated,
    apiKey,
    instanceId,
    handleMetrics,
    handleError,
    handleStatusChange,
    handleReconnectScheduled,
  ])

  useEffect(() => {
    if (!shouldStream) {
      serviceRef.current?.stop()
    }
  }, [shouldStream])

  useEffect(() => {
    return () => {
      stopRef.current?.()
      serviceRef.current?.stop()
    }
  }, [])

  const connect = useCallback(() => {
    setShouldStream(true)
    setError(null)
  }, [])

  // Disconnect is no longer exposed - streams stay connected when authenticated
  // To go offline, user must select "Offline" in the API key selector

  const retry = useCallback(() => {
    setError(null)
    setNextRetry(null)
    serviceRef.current?.manualReconnect()
  }, [])

  const runtimeURI = currentMetrics?.runtimeURI

  const trend = useMemo(
    () => trendRef.current.get(runtimeURI) ?? [],
    [runtimeURI, lastUpdatedAt], // Re-compute when lastUpdatedAt changes
  )

  return {
    currentMetrics,
    connectionState,
    error,
    lastUpdatedAt,
    nextRetry,
    trend,
    connect,
    retry,
    isStreaming: shouldStream,
    isAuthenticated,
  }
}

export default useMetricsStream
